// A Go Go por equipos — admin manager (web). Fully config-driven so any club
// can run its own team tournament. Mounted from ClubTournaments when a
// tournament's scoring_format === "a_go_go".
import { useState, useEffect, useCallback, Fragment } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, ClipboardList, BarChart3, Settings, Target, Info, Plus, Trash2, AlertTriangle, Save, Flag, MapPin, Printer, DollarSign, Upload, Download, FileText } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const api = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: "include", headers: AUTH(), ...opts });

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

export default function AGoGoManager({ tournament, canWrite, onChange }) {
  const tid = tournament.tournament_id;
  const [tab, setTab] = useState("teams");
  const tabs = [
    { k: "teams", l: "Equipos", icon: Users },
    { k: "capture", l: "Captura", icon: ClipboardList },
    { k: "starts", l: "Salidas", icon: MapPin },
    { k: "leaderboard", l: "Leaderboard", icon: BarChart3 },
    { k: "oyeses", l: "Oyeses", icon: Target },
    { k: "calcutta", l: "Calcuta", icon: DollarSign },
    { k: "config", l: "Configuración", icon: Settings },
    { k: "info", l: "Info", icon: Info },
  ];
  return (
    <div data-testid="agogo-manager">
      <div className="flex gap-1 mb-5 overflow-x-auto" data-testid="agogo-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:bg-gray-100"}`}
              data-testid={`agogo-tab-${t.k}`}>
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>
      {tab === "teams" && <TeamsTab tid={tid} canWrite={canWrite} />}
      {tab === "capture" && <CaptureTab tid={tid} canWrite={canWrite} />}
      {tab === "starts" && <StartsTab tid={tid} canWrite={canWrite} />}
      {tab === "leaderboard" && <LeaderboardTab tid={tid} />}
      {tab === "oyeses" && <OyesesTab tid={tid} canWrite={canWrite} />}
      {tab === "calcutta" && <CalcuttaTab tid={tid} canWrite={canWrite} />}
      {tab === "config" && <ConfigTab tid={tid} canWrite={canWrite} onChange={onChange} />}
      {tab === "info" && <InfoTab tid={tid} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------
const EMPTY_MEMBER = { name: "", sex: "M", is_pro: false, is_senior: false, plays_gold: false, tee_mark: "", index: "", playing_handicap: "" };

function TeamsTab({ tid, canWrite }) {
  const [data, setData] = useState({ teams: [], count: 0, team_cap: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // team object or {} for new
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api(`/club/tournaments/${tid}/teams`);
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const del = async (teamId) => {
    if (!window.confirm("¿Eliminar este equipo?")) return;
    const res = await api(`/club/tournaments/${tid}/teams/${teamId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Equipo eliminado"); load(); } else toast.error("Error");
  };

  if (loading) return <Spinner />;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#64748B]" data-testid="agogo-team-count">{data.count} / {data.team_cap} equipos</p>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setImporting(true)} data-testid="agogo-import-csv-btn"><Upload className="w-4 h-4 mr-1" /> Cargar CSV</Button>
            <Button size="sm" onClick={() => setEditing({})} data-testid="agogo-add-team-btn"><Plus className="w-4 h-4 mr-1" /> Equipo</Button>
          </div>
        )}
      </div>
      {data.teams.length === 0 ? (
        <Empty text="Sin equipos inscritos todavía." />
      ) : (
        <div className="space-y-2">
          {data.teams.map((t) => (
            <div key={t.team_id} className="border border-gray-200 rounded-xl p-4" data-testid={`agogo-team-${t.team_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-[#0F172A]">{t.name}</h4>
                    <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded font-semibold">HCP {t.hcp_total}</span>
                    {t.r1_date && <span className="text-xs text-[#64748B]">R1: {t.r1_date}</span>}
                    {t.validation?.warnings?.length > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1" data-testid={`agogo-team-warn-${t.team_id}`}>
                        <AlertTriangle className="w-3 h-3" /> {t.validation.warnings.length} aviso{t.validation.warnings.length > 1 ? "s" : ""}
                      </span>
                    )}
                    {t.validation?.ok && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">OK</span>}
                  </div>
                  {t.captain_name && <p className="text-xs text-[#94A3B8] mt-0.5">Capitán: {t.captain_name}</p>}
                  <p className="text-xs text-[#64748B] mt-1 truncate">{(t.members || []).map((m) => m.name).join(", ")}</p>
                  {t.validation?.warnings?.map((w, i) => (
                    <p key={i} className="text-[11px] text-amber-700 mt-0.5">• {w.message}</p>
                  ))}
                </div>
                {canWrite && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`agogo-edit-team-${t.team_id}`}>Editar</Button>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={() => del(t.team_id)} data-testid={`agogo-del-team-${t.team_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <TeamForm tid={tid} team={editing.team_id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportCsv tid={tid} onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
    </div>
  );
}

function TeamForm({ tid, team, onClose, onSaved }) {
  const [name, setName] = useState(team?.name || "");
  const [captain, setCaptain] = useState(team?.captain_name || "");
  const [r1Date, setR1Date] = useState(team?.r1_date || "");
  const [members, setMembers] = useState(
    team?.members?.length ? team.members.map((m) => ({ ...EMPTY_MEMBER, ...m, index: m.index ?? "", playing_handicap: m.playing_handicap ?? "" })) : [{ ...EMPTY_MEMBER }],
  );
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setMembers((p) => p.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addRow = () => setMembers((p) => [...p, { ...EMPTY_MEMBER }]);
  const delRow = (i) => setMembers((p) => p.filter((_, j) => j !== i));

  const doValidate = async () => {
    const res = await api(`/club/tournaments/${tid}/teams/validate`, { method: "POST", body: JSON.stringify({ members }) });
    if (res.ok) setPreview(await res.json());
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Nombre del equipo requerido");
    setSaving(true);
    try {
      const body = { name, captain_name: captain, r1_date: r1Date, members, confirm_override: true, override_closed: true };
      const url = team ? `/club/tournaments/${tid}/teams/${team.team_id}` : `/club/tournaments/${tid}/teams`;
      const res = await api(url, { method: team ? "PUT" : "POST", body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      const warns = d.validation?.warnings?.length || 0;
      toast.success(team ? "Equipo actualizado" : warns ? `Equipo creado con ${warns} aviso(s)` : "Equipo creado");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="agogo-team-form">
        <div className="sticky top-0 bg-white border-b px-6 py-4 font-bold">{team ? "Editar equipo" : "Nuevo equipo"}</div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Nombre del equipo *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="agogo-team-name" /></div>
            <div><Label className="text-xs">Capitán</Label><Input value={captain} onChange={(e) => setCaptain(e.target.value)} data-testid="agogo-team-captain" /></div>
            <div><Label className="text-xs">Fecha R1</Label><Input value={r1Date} onChange={(e) => setR1Date(e.target.value)} placeholder="Jue / Vie" data-testid="agogo-team-r1date" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Integrantes</Label>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="agogo-add-member"><Plus className="w-3.5 h-3.5 mr-1" /> Integrante</Button>
            </div>
            <div className="space-y-2">
              {members.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center border border-gray-100 rounded-lg p-2" data-testid={`agogo-member-${i}`}>
                  <Input className="col-span-3 h-8" placeholder="Nombre" value={m.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`agogo-member-name-${i}`} />
                  <select className="col-span-1 h-8 border rounded text-xs" value={m.sex} onChange={(e) => upd(i, { sex: e.target.value })} data-testid={`agogo-member-sex-${i}`}>
                    <option value="M">H</option><option value="F">M</option>
                  </select>
                  <Input className="col-span-2 h-8" placeholder="Marca" value={m.tee_mark} onChange={(e) => upd(i, { tee_mark: e.target.value })} title="azules/blancas/rojas/doradas" data-testid={`agogo-member-tee-${i}`} />
                  <Input className="col-span-1 h-8" placeholder="Idx" value={m.index} onChange={(e) => upd(i, { index: e.target.value })} data-testid={`agogo-member-index-${i}`} />
                  <Input className="col-span-1 h-8" placeholder="HCP" value={m.playing_handicap} onChange={(e) => upd(i, { playing_handicap: e.target.value })} title="HCP de juego publicado (import)" data-testid={`agogo-member-hcp-${i}`} />
                  <label className="col-span-1 text-[10px] flex items-center gap-0.5"><input type="checkbox" checked={!!m.is_pro} onChange={(e) => upd(i, { is_pro: e.target.checked })} />Pro</label>
                  <label className="col-span-1 text-[10px] flex items-center gap-0.5"><input type="checkbox" checked={!!m.is_senior} onChange={(e) => upd(i, { is_senior: e.target.checked })} />Sr</label>
                  <label className="col-span-1 text-[10px] flex items-center gap-0.5"><input type="checkbox" checked={!!m.plays_gold} onChange={(e) => upd(i, { plays_gold: e.target.checked })} />Dor</label>
                  <button className="col-span-1 text-red-500" onClick={() => delRow(i)} data-testid={`agogo-del-member-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-1">Captura Idx para calcular HCP de juego con la marca, o HCP para usar el publicado (import). H/M = sexo, Pro/Sr/Dor = profesional/senior/doradas.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={doValidate} data-testid="agogo-validate-btn">Validar</Button>
            {preview && (
              <span className={`text-xs px-2 py-1 rounded ${preview.ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="agogo-validate-result">
                HCP {preview.hcp_total} · {preview.ok ? "Cumple" : `${preview.warnings.length} aviso(s)`}
              </span>
            )}
          </div>
          {preview?.warnings?.map((w, i) => <p key={i} className="text-[11px] text-amber-700" data-testid={`agogo-warn-${w.code}`}>• {w.message}</p>)}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="agogo-save-team-btn">{saving ? "Guardando…" : "Guardar equipo"}</Button>
        </div>
      </div>
    </div>
  );
}

const CSV_TEMPLATE = `Equipo,Capitan,Fecha R1,Nombre,Sexo,Indice,HCP,Pro,Senior,Dorado,Marca
Los Pinos,Juan Perez,Jue,Juan Perez,H,,18,,,,azules
Los Pinos,,,Maria Lopez,D,,22,,,si,rojas
Los Pinos,,,Pedro Gomez,H,12.4,,,,,azules
Aguilas,Ana Ruiz,Vie,Ana Ruiz,D,,20,,,,rojas
Aguilas,,,Luis Mora,H,,16,si,,,azules
`;

function ImportCsv({ tid, onClose, onDone }) {
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla_equipos_agogo.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const text = await f.text();
    setCsvText(text);
    setParsing(true); setPreview(null);
    try {
      const res = await api(`/club/tournaments/${tid}/teams/import-csv`, { method: "POST", body: JSON.stringify({ csv: text, dry_run: true }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "No se pudo leer el CSV");
      setPreview(d);
    } catch (err) { toast.error(err.message); } finally { setParsing(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const res = await api(`/club/tournaments/${tid}/teams/import-csv`, { method: "POST", body: JSON.stringify({ csv: csvText, dry_run: false }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`${d.created} equipo(s) importado(s)`);
      onDone();
    } catch (err) { toast.error(err.message); } finally { setCommitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="agogo-import-csv">
        <div className="sticky top-0 bg-white border-b px-6 py-4 font-bold flex items-center gap-2"><Upload className="w-4 h-4" /> Importar equipos por CSV</div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-[#64748B] space-y-1">
            <p className="font-semibold text-[#0F172A]">Formato: una fila por jugador, agrupados por la columna <b>Equipo</b>.</p>
            <p>Columnas: <b>Equipo</b>, Capitan, Fecha R1, <b>Nombre</b>, Sexo, Indice, HCP, Pro, Senior, Dorado, Marca.</p>
            <p>Captura <b>Indice</b> para calcular el HCP de juego con la marca, o <b>HCP</b> para usar el publicado. Pro/Senior/Dorado: escribe "si" si aplica. Sexo: H = hombre, D = dama.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="agogo-csv-template"><Download className="w-3.5 h-3.5 mr-1" /> Descargar plantilla</Button>
            <label className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-[#1B4D3E] text-[#1B4D3E] cursor-pointer hover:bg-[#1B4D3E]/5" data-testid="agogo-csv-filelabel">
              <FileText className="w-3.5 h-3.5" /> {fileName || "Elegir archivo CSV"}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="agogo-csv-file" />
            </label>
          </div>
          {parsing && <Spinner />}
          {preview && (
            <div data-testid="agogo-csv-preview">
              {preview.errors?.length > 0 && (
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {preview.errors.map((er, i) => <p key={i}>• {er}</p>)}
                </div>
              )}
              <p className="text-sm font-semibold mb-2">Vista previa: {preview.count} equipo(s)</p>
              {preview.count === 0 ? <Empty text="No se detectaron equipos en el archivo." /> : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {preview.teams.map((t, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-2" data-testid={`agogo-csv-team-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm">{t.name}</span>
                        <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">HCP {t.hcp_total}</span>
                        <span className="text-xs text-[#64748B]">{t.members_count} integrante(s)</span>
                        {t.captain_name && <span className="text-xs text-[#94A3B8]">Cap: {t.captain_name}</span>}
                        {t.warnings?.length > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{t.warnings.length} aviso(s)</span>}
                      </div>
                      <p className="text-[11px] text-[#64748B] mt-0.5">{t.members.map((m) => `${m.name} (${m.sex === "F" ? "D" : "H"}${m.playing_handicap != null ? " · HCP " + m.playing_handicap : ""})`).join(", ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={commit} disabled={committing || !preview || preview.count === 0} data-testid="agogo-csv-import-btn">{committing ? "Importando…" : `Importar ${preview?.count || 0} equipo(s)`}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capture (one score per hole per team)
// ---------------------------------------------------------------------------
function CaptureTab({ tid, canWrite }) {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [round, setRound] = useState(1);
  const [holes, setHoles] = useState({});
  const [card, setCard] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api(`/club/tournaments/${tid}/teams`).then((r) => r.json()).then((d) => { setTeams(d.teams || []); if (d.teams?.[0]) setTeamId(d.teams[0].team_id); }); }, [tid]);

  const loadCard = useCallback(async () => {
    if (!teamId) return;
    const res = await api(`/club/tournaments/${tid}/teams/${teamId}/scorecard`);
    if (res.ok) {
      const d = await res.json(); setCard(d);
      const h = (d.rounds?.[round]?.holes) || {};
      setHoles(Object.fromEntries(Object.entries(h).map(([k, v]) => [k, v == null ? "" : v])));
    }
  }, [tid, teamId, round]);
  useEffect(() => { loadCard(); }, [loadCard]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api(`/club/tournaments/${tid}/teams/${teamId}/scores`, { method: "PUT", body: JSON.stringify({ round_num: round, holes }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`Guardado · Total ${d.total} (${d.captured}/18)`);
      loadCard();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const total = HOLES.reduce((a, h) => a + (parseInt(holes[h]) || 0), 0);
  const captured = HOLES.filter((h) => holes[h] !== "" && holes[h] != null).length;

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div><Label className="text-xs">Equipo</Label>
          <select className="h-9 border rounded px-2 text-sm min-w-[200px]" value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="agogo-capture-team">
            {teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Ronda</Label>
          <select className="h-9 border rounded px-2 text-sm" value={round} onChange={(e) => setRound(parseInt(e.target.value))} data-testid="agogo-capture-round">
            <option value={1}>R1 (clasificación)</option><option value={2}>R2 (final)</option>
          </select>
        </div>
        <div className="text-sm font-semibold text-[#1B4D3E]">Total {total} · {captured}/18</div>
      </div>
      <div className="grid grid-cols-9 gap-1.5 mb-4" data-testid="agogo-capture-grid">
        {HOLES.map((h) => (
          <div key={h} className="text-center">
            <div className="text-[10px] text-[#94A3B8]">{h}</div>
            <input type="number" min="1" max="30" value={holes[h] ?? ""} disabled={!canWrite}
              onChange={(e) => setHoles((p) => ({ ...p, [h]: e.target.value }))}
              className="w-full text-center font-bold text-sm border border-gray-200 rounded py-1 focus:border-[#1B4D3E] focus:outline-none"
              data-testid={`agogo-hole-${h}`} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#94A3B8] mb-3">Un score por hoyo por equipo, gross. Hoyo vacío = pendiente. Se guarda tal cual lo capturas (sin tope automático).</p>
      {canWrite && <Button onClick={save} disabled={saving || !teamId} data-testid="agogo-save-scores-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar tarjeta"}</Button>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Salidas (shotgun starts — assign starting hole + slot A/B/C… per team, per round)
// ---------------------------------------------------------------------------
function StartsTab({ tid, canWrite }) {
  const [teams, setTeams] = useState([]);
  const [round, setRound] = useState(1);
  const [holeByTeam, setHoleByTeam] = useState({});
  const [startTime, setStartTime] = useState("");
  const [trnName, setTrnName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [td, sd] = await Promise.all([
        api(`/club/tournaments/${tid}/teams`).then((r) => r.json()),
        api(`/club/tournaments/${tid}/agogo/starts?round_num=${round}`).then((r) => r.json()),
      ]);
      setTeams(td.teams || []);
      setStartTime(sd.start_time || "");
      setTrnName(sd.tournament_name || "");
      const map = {};
      (sd.holes || []).forEach((h) => h.teams.forEach((t) => { map[t.team_id] = String(h.hole); }));
      setHoleByTeam(map);
    } finally { setLoading(false); }
  }, [tid, round]);
  useEffect(() => { load(); }, [load]);

  // Live slot preview: group by hole following the team list order.
  const slotOf = (teamId) => {
    const hole = holeByTeam[teamId];
    if (!hole) return null;
    const sameHole = teams.filter((t) => holeByTeam[t.team_id] === hole);
    const idx = sameHole.findIndex((t) => t.team_id === teamId);
    return String.fromCharCode(65 + idx); // A, B, C…
  };

  const autoGenerate = async () => {
    const res = await api(`/club/tournaments/${tid}/agogo/starts/generate`, { method: "POST", body: JSON.stringify({ round_num: round }) });
    if (res.ok) { toast.success(round >= 2 ? "Salidas generadas por score" : "Salidas generadas por rifa"); load(); } else toast.error("Error");
  };
  const save = async () => {
    setSaving(true);
    try {
      const assignments = teams.filter((t) => holeByTeam[t.team_id]).map((t) => ({ team_id: t.team_id, hole: parseInt(holeByTeam[t.team_id]) }));
      await api(`/club/tournaments/${tid}/agogo/starts/time`, { method: "PUT", body: JSON.stringify({ round_num: round, start_time: startTime }) });
      const res = await api(`/club/tournaments/${tid}/agogo/starts`, { method: "PUT", body: JSON.stringify({ round_num: round, assignments }) });
      if (!res.ok) throw new Error("Error");
      toast.success("Salidas guardadas"); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const [notifying, setNotifying] = useState(false);
  const notifyStarts = async () => {
    if (!window.confirm(`¿Enviar a cada jugador su hoyo y hora de salida de la Ronda ${round}? (notificación in-app + push)`)) return;
    setNotifying(true);
    try {
      const res = await api(`/club/tournaments/${tid}/agogo/starts/notify`, { method: "POST", body: JSON.stringify({ round_num: round }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error");
      toast.success(`Aviso enviado a ${d.players_notified} jugador(es)${d.teams_without_start ? ` · ${d.teams_without_start} equipo(s) sin salida` : ""}`);
    } catch (e) { toast.error(e.message); } finally { setNotifying(false); }
  };

  const printSheet = () => {
    const groups = {};
    teams.forEach((t) => { const h = holeByTeam[t.team_id]; if (h) (groups[h] = groups[h] || []).push(t.name); });
    const holesHtml = Array.from({ length: 18 }, (_, i) => i + 1)
      .filter((h) => groups[h])
      .map((h) => {
        const rows = groups[h].map((nm, i) => `<tr><td class="slot">${String.fromCharCode(65 + i)}</td><td>${nm}</td></tr>`).join("");
        return `<div class="hole"><div class="hh">Hoyo ${h}</div><table>${rows}</table></div>`;
      }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Salidas</title>
      <style>
        body{font-family:system-ui,Arial,sans-serif;color:#0F172A;padding:24px}
        h1{font-size:20px;margin:0 0 2px} .sub{color:#64748B;font-size:13px;margin-bottom:16px}
        .time{display:inline-block;background:#1B4D3E;color:#fff;padding:4px 12px;border-radius:8px;font-weight:700;margin-left:8px}
        .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
        .hole{border:1px solid #cbd5e1;border-radius:8px;padding:8px;break-inside:avoid}
        .hh{font-weight:700;color:#1B4D3E;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:4px}
        table{width:100%;border-collapse:collapse;font-size:13px} td{padding:2px 4px}
        .slot{font-weight:700;color:#1B4D3E;width:22px}
        @media print{.grid{grid-template-columns:repeat(3,1fr)}}
      </style></head><body>
      <h1>${trnName || "Torneo"} — Hoja de Salidas</h1>
      <div class="sub">Ronda ${round} · Escopetazo ${startTime ? `<span class="time">${startTime}</span>` : "(sin hora)"}</div>
      ${holesHtml || "<p>Sin salidas asignadas.</p>"}
      <script>window.onload=function(){window.print()}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
    w.document.write(html); w.document.close();
  };

  if (loading) return <Spinner />;

  // Grouped preview
  const byHole = {};
  teams.forEach((t) => { const h = holeByTeam[t.team_id]; if (h) (byHole[h] = byHole[h] || []).push(t); });

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div><Label className="text-xs">Ronda</Label>
          <select className="h-9 border rounded px-2 text-sm" value={round} onChange={(e) => setRound(parseInt(e.target.value))} data-testid="agogo-starts-round">
            <option value={1}>R1 (rifa)</option><option value={2}>R2 (por score)</option>
          </select>
        </div>
        <div><Label className="text-xs">Hora del escopetazo</Label>
          <input type="time" value={startTime} disabled={!canWrite} onChange={(e) => setStartTime(e.target.value)}
            className="h-9 border rounded px-2 text-sm block" data-testid="agogo-starts-time" />
        </div>
        {canWrite && <Button variant="outline" size="sm" onClick={autoGenerate} data-testid="agogo-starts-generate">Auto-generar {round >= 2 ? "(por score)" : "(rifa)"}</Button>}
        {canWrite && <Button size="sm" onClick={save} disabled={saving} data-testid="agogo-starts-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar salidas"}</Button>}
        <Button variant="outline" size="sm" onClick={printSheet} data-testid="agogo-starts-print"><Printer className="w-4 h-4 mr-1" /> Imprimir / Exportar</Button>
        {canWrite && <Button variant="outline" size="sm" onClick={notifyStarts} disabled={notifying} data-testid="agogo-starts-notify"><Flag className="w-4 h-4 mr-1" /> {notifying ? "Enviando…" : "Avisar a jugadores"}</Button>}
      </div>
      <p className="text-[11px] text-[#94A3B8] mb-3">Escopetazo: todos salen a la misma hora. Asigna el hoyo de salida a cada equipo (1-18). Varios equipos pueden compartir hoyo → se etiquetan A, B, C…</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assign per team */}
        <div>
          <h4 className="text-sm font-bold mb-2">Equipos</h4>
          <div className="space-y-1.5" data-testid="agogo-starts-teams">
            {teams.map((t) => (
              <div key={t.team_id} className="flex items-center gap-2 border border-gray-100 rounded-lg px-3 py-1.5">
                <span className="flex-1 text-sm truncate">{t.name}</span>
                {holeByTeam[t.team_id] && <span className="text-xs font-bold text-[#1B4D3E]">H{holeByTeam[t.team_id]}-{slotOf(t.team_id)}</span>}
                <input type="number" min="1" max="18" value={holeByTeam[t.team_id] || ""} disabled={!canWrite}
                  onChange={(e) => setHoleByTeam((p) => ({ ...p, [t.team_id]: e.target.value }))}
                  placeholder="Hoyo" className="w-20 h-8 text-center border rounded text-sm" data-testid={`agogo-start-hole-${t.team_id}`} />
              </div>
            ))}
            {teams.length === 0 && <Empty text="Sin equipos todavía." />}
          </div>
        </div>
        {/* Grouped by hole */}
        <div>
          <h4 className="text-sm font-bold mb-2">Por hoyo</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="agogo-starts-grid">
            {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
              const list = (byHole[h] || []);
              return (
                <div key={h} className={`border rounded-lg p-2 ${list.length ? "border-[#1B4D3E]/30 bg-[#1B4D3E]/5" : "border-gray-100"}`} data-testid={`agogo-starts-hole-${h}`}>
                  <div className="text-xs font-bold text-[#64748B] flex items-center gap-1"><Flag className="w-3 h-3" /> Hoyo {h}</div>
                  {list.map((t, i) => (
                    <div key={t.team_id} className="text-[11px] text-[#0F172A] truncate"><span className="font-bold text-[#1B4D3E]">{String.fromCharCode(65 + i)}</span> {t.name}</div>
                  ))}
                  {!list.length && <div className="text-[10px] text-[#94A3B8]">—</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard
// ---------------------------------------------------------------------------
function LeaderboardTab({ tid }) {
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api(`/club/tournaments/${tid}/agogo/leaderboard`).then((r) => r.json()).then((d) => { setLb(d); setLoading(false); }); }, [tid]);
  if (loading) return <Spinner />;
  if (!lb?.rows?.length) return <Empty text="Aún no hay tarjetas capturadas." />;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-1 rounded font-semibold">{lb.phase === "final" ? "Clasificación final (36h)" : "Clasificación R1 (corte)"}</span>
        <span className="text-xs text-[#64748B]">Par {lb.par_total} · Corte: {lb.cut_teams}</span>
        {lb.has_unresolved_tie && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Empate sin resolver — revisar organizador</span>}
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-xl" data-testid="agogo-leaderboard">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[#64748B]"><tr>
            <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Equipo</th>
            <th className="px-3 py-2 text-right">R1</th><th className="px-3 py-2 text-right">R2</th>
            <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">vs Par</th>
          </tr></thead>
          <tbody>
            {lb.rows.map((r, i) => (
              <Fragment key={r.team_id}>
                {i === lb.cut_teams && lb.rows.length > lb.cut_teams && (
                  <tr data-testid="agogo-cut-line"><td colSpan={6} className="px-3 py-1 text-center text-[11px] font-bold text-red-600 bg-red-50 border-y border-red-200">— LÍNEA DE CORTE ({lb.cut_teams}) —</td></tr>
                )}
                <tr className={`border-t ${r.made_cut ? "" : "opacity-60"} ${r.tie_unresolved ? "bg-amber-50" : ""}`} data-testid={`agogo-lb-row-${r.position}`}>
                  <td className="px-3 py-2 font-bold">{r.position}</td>
                  <td className="px-3 py-2">{r.name} {r.tie_unresolved && <AlertTriangle className="w-3 h-3 inline text-amber-600" />}</td>
                  <td className="px-3 py-2 text-right">{r.r1_complete ? r.r1_total : <span className="text-amber-500">{r.r1_total}·{r.r1_pending?.length}p</span>}</td>
                  <td className="px-3 py-2 text-right">{r.r2_complete ? r.r2_total : (r.r2_total ? <span className="text-amber-500">{r.r2_total}·{r.r2_pending?.length}p</span> : "—")}</td>
                  <td className="px-3 py-2 text-right font-bold">{r.combined_total}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#1B4D3E]">{r.to_par == null ? "—" : (r.to_par > 0 ? `+${r.to_par}` : r.to_par)}</td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Oyeses
// ---------------------------------------------------------------------------
function OyesesTab({ tid, canWrite }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api(`/club/tournaments/${tid}/agogo/oyeses`).then((r) => r.json()).then(setData), [tid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Spinner />;
  return (
    <div className="space-y-6">
      {data.holes.map((h) => <OyesHole key={h.hole} tid={tid} hole={h} canWrite={canWrite} onSaved={load} />)}
    </div>
  );
}

function OyesHole({ tid, hole, canWrite, onSaved }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const sorted = (hole.entries || [])
      .map((e) => ({ ...e, distance_m: e.distance_m ?? "" }))
      .sort((a, b) => (parseFloat(a.distance_m) || Infinity) - (parseFloat(b.distance_m) || Infinity));
    setRows(sorted);
  }, [hole]);
  const addRow = () => setRows((p) => [...p, { player_name: "", team_name: "", distance_m: "" }]);
  const upd = (i, patch) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const res = await api(`/club/tournaments/${tid}/agogo/oyeses`, { method: "PUT", body: JSON.stringify({ hole: hole.hole, entries: rows }) });
    if (res.ok) { toast.success(`Oyes hoyo ${hole.hole} guardado`); onSaved(); } else toast.error("Error");
  };
  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid={`agogo-oyes-${hole.hole}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold flex items-center gap-1.5"><Flag className="w-4 h-4 text-[#1B4D3E]" /> Hoyo {hole.hole} · {hole.places} lugares (menor distancia)</h4>
        {canWrite && <Button variant="outline" size="sm" onClick={addRow}><Plus className="w-3.5 h-3.5 mr-1" /> Tiro</Button>}
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
            <span className="col-span-1 text-xs font-bold text-[#1B4D3E]">{i + 1}</span>
            <Input className="col-span-5 h-8" placeholder="Jugador" value={r.player_name} disabled={!canWrite} onChange={(e) => upd(i, { player_name: e.target.value })} data-testid={`agogo-oyes-${hole.hole}-name-${i}`} />
            <Input className="col-span-4 h-8" placeholder="Equipo" value={r.team_name || ""} disabled={!canWrite} onChange={(e) => upd(i, { team_name: e.target.value })} />
            <Input className="col-span-2 h-8" placeholder="m" value={r.distance_m} disabled={!canWrite} onChange={(e) => upd(i, { distance_m: e.target.value })} data-testid={`agogo-oyes-${hole.hole}-dist-${i}`} />
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-[#94A3B8]">Sin tiros registrados.</p>}
      </div>
      {canWrite && <Button size="sm" className="mt-3" onClick={save} data-testid={`agogo-oyes-save-${hole.hole}`}><Save className="w-3.5 h-3.5 mr-1" /> Guardar hoyo {hole.hole}</Button>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calcuta (team auction)
// ---------------------------------------------------------------------------
function CalcuttaTab({ tid, canWrite }) {
  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [teams, setTeams] = useState([]);
  const [draft, setDraft] = useState({}); // team_id -> {bidder_name, amount}
  const [savingCfg, setSavingCfg] = useState(false);
  const [busyTeam, setBusyTeam] = useState(null);

  const load = useCallback(async () => {
    const [c, td] = await Promise.all([
      api(`/club/tournaments/${tid}/agogo/calcutta`).then((r) => r.json()),
      api(`/club/tournaments/${tid}/teams`).then((r) => r.json()),
    ]);
    setCfg(c.config); setSummary(c.summary);
    setTeams(td.teams || []);
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const money = (v) => {
    const cur = cfg?.currency || "MXN";
    try { return new Intl.NumberFormat("es-MX", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0); }
    catch { return `${v} ${cur}`; }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const res = await api(`/club/tournaments/${tid}/agogo/calcutta-config`, { method: "PUT", body: JSON.stringify({ calcutta: cfg }) });
      const d = await res.json();
      if (!res.ok) throw new Error("Error");
      setSummary(d.summary); toast.success("Configuración de calcuta guardada");
    } catch (e) { toast.error(e.message); } finally { setSavingCfg(false); }
  };
  const addBid = async (teamId) => {
    const d = draft[teamId] || {};
    const amount = parseFloat(d.amount);
    if (!d.bidder_name?.trim() || !(amount > 0)) { toast.error("Ingresa postor y monto"); return; }
    setBusyTeam(teamId);
    try {
      const res = await api(`/club/tournaments/${tid}/teams/${teamId}/calcutta/bids`, {
        method: "POST", body: JSON.stringify({ bidder_name: d.bidder_name.trim(), amount }),
      });
      if (!res.ok) throw new Error("Error");
      setDraft((p) => ({ ...p, [teamId]: { bidder_name: "", amount: "" } }));
      await load();
    } catch { toast.error("No se pudo registrar la puja"); } finally { setBusyTeam(null); }
  };
  const delBid = async (teamId, bidId) => {
    setBusyTeam(teamId);
    try {
      await api(`/club/tournaments/${tid}/teams/${teamId}/calcutta/bids/${bidId}`, { method: "DELETE" });
      await load();
    } catch { toast.error("Error"); } finally { setBusyTeam(null); }
  };
  const setWinner = async (teamId, bidId) => {
    setBusyTeam(teamId);
    try {
      const res = await api(`/club/tournaments/${tid}/teams/${teamId}/calcutta/winner`, {
        method: "PUT", body: JSON.stringify({ bid_id: bidId }),
      });
      if (!res.ok) throw new Error("Error");
      toast.success(bidId ? "Ganador marcado" : "Venta anulada");
      await load();
    } catch { toast.error("Error"); } finally { setBusyTeam(null); }
  };

  if (!cfg || !summary) return <Spinner />;
  const payoutTotal = (cfg.payouts || []).reduce((a, p) => a + (Number(p.percent) || 0), 0);
  const houseTotal = (cfg.house_cuts || []).reduce((a, h) => a + (Number(h.percent) || 0), 0);
  const sp = cfg.starting_price || { enabled: false, mode: "count", order: "hcp_desc", tiers: [] };
  const setSp = (next) => setCfg({ ...cfg, starting_price: { ...sp, ...next } });

  return (
    <div className="space-y-6" data-testid="agogo-calcutta">
      <Section title="Configuración de la calcuta">
        <div className="flex items-center gap-4 flex-wrap mb-3">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={!!cfg.enabled} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="agogo-calcutta-enabled" />
            Calcuta activa (visible para jugadores)
          </label>
          <div className="flex items-center gap-1"><Label className="text-xs">Moneda</Label>
            <Input className="w-24 h-8" value={cfg.currency} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} data-testid="agogo-calcutta-currency" /></div>
        </div>

        {/* House cuts */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs font-semibold">Cortes a terceros (club, fundación…) — % de la bolsa total</Label>
            {canWrite && <Button variant="outline" size="sm" onClick={() => setCfg({ ...cfg, house_cuts: [...(cfg.house_cuts || []), { label: "", percent: 0 }] })} data-testid="agogo-add-house"><Plus className="w-3.5 h-3.5 mr-1" /> Corte</Button>}
          </div>
          {(cfg.house_cuts || []).map((h, i) => (
            <div key={i} className="flex items-center gap-2 mb-1" data-testid={`agogo-house-${i}`}>
              <Input className="flex-1 h-8" placeholder="Ej: Club / Fundación" value={h.label} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, house_cuts: cfg.house_cuts.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} />
              <Input className="w-20 h-8" type="number" value={h.percent} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, house_cuts: cfg.house_cuts.map((x, j) => j === i ? { ...x, percent: parseFloat(e.target.value) || 0 } : x) })} data-testid={`agogo-house-pct-${i}`} />
              <span className="text-xs text-[#94A3B8]">%</span>
              {canWrite && <button className="text-red-500" onClick={() => setCfg({ ...cfg, house_cuts: cfg.house_cuts.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
          <p className="text-[11px] text-[#94A3B8]">Suma cortes: {houseTotal}% de la bolsa.</p>
        </div>

        {/* Payouts */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs font-semibold">Premiación por posición — % de la bolsa NETA (tras cortes)</Label>
            {canWrite && <Button variant="outline" size="sm" onClick={() => setCfg({ ...cfg, payouts: [...(cfg.payouts || []), { position: (cfg.payouts?.length || 0) + 1, percent: 0 }] })} data-testid="agogo-add-payout"><Plus className="w-3.5 h-3.5 mr-1" /> Lugar</Button>}
          </div>
          {(cfg.payouts || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2 mb-1" data-testid={`agogo-payout-${i}`}>
              <span className="text-xs font-bold text-[#1B4D3E] w-16">Lugar</span>
              <Input className="w-16 h-8" type="number" value={p.position} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, payouts: cfg.payouts.map((x, j) => j === i ? { ...x, position: parseInt(e.target.value) || 1 } : x) })} />
              <Input className="w-20 h-8" type="number" value={p.percent} disabled={!canWrite} onChange={(e) => setCfg({ ...cfg, payouts: cfg.payouts.map((x, j) => j === i ? { ...x, percent: parseFloat(e.target.value) || 0 } : x) })} data-testid={`agogo-payout-pct-${i}`} />
              <span className="text-xs text-[#94A3B8]">%</span>
              {canWrite && <button className="text-red-500" onClick={() => setCfg({ ...cfg, payouts: cfg.payouts.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
          <p className={`text-[11px] ${payoutTotal === 100 ? "text-emerald-600" : "text-amber-600"}`}>Suma premiación: {payoutTotal}% {payoutTotal !== 100 ? "(se recomienda 100%)" : ""}</p>
        </div>

        {/* Starting price ladder */}
        <div className="mt-4 border-t border-gray-200 pt-3">
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <input type="checkbox" checked={!!sp.enabled} disabled={!canWrite} onChange={(e) => setSp({ enabled: e.target.checked })} data-testid="agogo-sp-enabled" />
            Precio de salida por equipo (puja inicial)
          </label>
          {sp.enabled && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1"><Label className="text-xs">Agrupar por</Label>
                  <select className="h-8 border border-gray-300 rounded-md text-sm px-2" value={sp.mode} disabled={!canWrite} onChange={(e) => setSp({ mode: e.target.value })} data-testid="agogo-sp-mode">
                    <option value="count">Cantidad de equipos</option>
                    <option value="hcp_range">Rango de HCP</option>
                  </select></div>
                <div className="flex items-center gap-1"><Label className="text-xs">Orden</Label>
                  <select className="h-8 border border-gray-300 rounded-md text-sm px-2" value={sp.order} disabled={!canWrite} onChange={(e) => setSp({ order: e.target.value })} data-testid="agogo-sp-order">
                    <option value="hcp_desc">Mayor HCP → Menor</option>
                    <option value="hcp_asc">Menor HCP → Mayor</option>
                  </select></div>
                {canWrite && <Button variant="outline" size="sm" onClick={() => setSp({ tiers: [...(sp.tiers || []), sp.mode === "count" ? { amount: 0, count: 10 } : { amount: 0, hcp_from: null, hcp_to: null }] })} data-testid="agogo-sp-add-tier"><Plus className="w-3.5 h-3.5 mr-1" /> Tramo</Button>}
              </div>
              {(sp.tiers || []).map((tr, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`agogo-sp-tier-${i}`}>
                  <span className="text-xs text-[#64748B]">Salida</span>
                  <Input className="w-24 h-8" type="number" placeholder="Monto" value={tr.amount ?? ""} disabled={!canWrite} onChange={(e) => setSp({ tiers: sp.tiers.map((x, j) => j === i ? { ...x, amount: parseFloat(e.target.value) || 0 } : x) })} data-testid={`agogo-sp-amount-${i}`} />
                  {sp.mode === "count" ? (
                    <>
                      <span className="text-xs text-[#64748B]">para los primeros</span>
                      <Input className="w-20 h-8" type="number" placeholder={i === sp.tiers.length - 1 ? "resto" : "N"} value={tr.count ?? ""} disabled={!canWrite} onChange={(e) => setSp({ tiers: sp.tiers.map((x, j) => j === i ? { ...x, count: parseInt(e.target.value) || null } : x) })} data-testid={`agogo-sp-count-${i}`} />
                      <span className="text-xs text-[#64748B]">equipos</span>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-[#64748B]">HCP de</span>
                      <Input className="w-16 h-8" type="number" placeholder="máx" value={tr.hcp_from ?? ""} disabled={!canWrite} onChange={(e) => setSp({ tiers: sp.tiers.map((x, j) => j === i ? { ...x, hcp_from: e.target.value === "" ? null : parseFloat(e.target.value) } : x) })} data-testid={`agogo-sp-hcpfrom-${i}`} />
                      <span className="text-xs text-[#64748B]">a</span>
                      <Input className="w-16 h-8" type="number" placeholder="mín" value={tr.hcp_to ?? ""} disabled={!canWrite} onChange={(e) => setSp({ tiers: sp.tiers.map((x, j) => j === i ? { ...x, hcp_to: e.target.value === "" ? null : parseFloat(e.target.value) } : x) })} data-testid={`agogo-sp-hcpto-${i}`} />
                    </>
                  )}
                  {canWrite && <button className="text-red-500" onClick={() => setSp({ tiers: sp.tiers.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
              <p className="text-[11px] text-[#94A3B8]">{sp.mode === "count" ? "Los equipos se ordenan por HCP; el último tramo cubre a los equipos restantes." : "Cada equipo toma el precio del tramo cuyo rango de HCP lo contiene."}</p>
            </div>
          )}
        </div>
        {canWrite && <Button className="mt-3" onClick={saveCfg} disabled={savingCfg} data-testid="agogo-save-calcutta-cfg"><Save className="w-4 h-4 mr-1" /> {savingCfg ? "Guardando…" : "Guardar configuración"}</Button>}
      </Section>

      <Section title="Subasta en vivo — historial de pujas">
        <div className="space-y-3">
          {teams.map((t) => {
            const bids = [...(t.bids || [])].sort((a, b) => b.amount - a.amount);
            const d = draft[t.team_id] || {};
            const busy = busyTeam === t.team_id;
            const highest = bids.length ? bids[0].amount : null;
            const amt = parseFloat(d.amount);
            const invalid = d.amount !== "" && d.amount != null && !Number.isNaN(amt) && ((highest != null && amt <= highest) || (highest == null && t.starting_price != null && amt < t.starting_price));
            return (
              <div key={t.team_id} className="border border-gray-200 rounded-xl p-3" data-testid={`agogo-auction-${t.team_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{t.name}</span>
                    {t.starting_price != null && <span className="text-[11px] font-semibold text-[#1B4D3E] bg-[#1B4D3E]/5 px-1.5 py-0.5 rounded" data-testid={`agogo-start-price-${t.team_id}`}>Salida {money(t.starting_price)}</span>}
                  </div>
                  {t.sale_amount != null ? (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full" data-testid={`agogo-sold-${t.team_id}`}>
                      Vendido: {money(t.sale_amount)} · {t.owner_name || "—"}
                    </span>
                  ) : (
                    <span className="text-xs text-[#94A3B8]">Sin vender</span>
                  )}
                </div>
                {bids.length > 0 ? (
                  <div className="space-y-1 mb-2">
                    {bids.map((b) => {
                      const win = t.winning_bid_id === b.bid_id;
                      return (
                        <div key={b.bid_id} className={`flex items-center gap-2 text-sm px-2 py-1 rounded-lg ${win ? "bg-emerald-50" : "bg-gray-50"}`} data-testid={`agogo-bid-${b.bid_id}`}>
                          <span className="flex-1 truncate">{b.bidder_name}</span>
                          <span className="font-semibold">{money(b.amount)}</span>
                          <span className="text-[10px] text-[#94A3B8]">{(() => { try { return new Date(b.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } })()}</span>
                          {canWrite && (
                            <>
                              <button className={`text-[11px] font-semibold ${win ? "text-emerald-700" : "text-[#1B4D3E]"}`} disabled={busy} onClick={() => setWinner(t.team_id, win ? null : b.bid_id)} data-testid={`agogo-win-${b.bid_id}`}>
                                {win ? "★ Ganadora" : "Marcar ganadora"}
                              </button>
                              <button className="text-red-500" disabled={busy} onClick={() => delBid(t.team_id, b.bid_id)} data-testid={`agogo-del-bid-${b.bid_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-xs text-[#94A3B8] mb-2">Aún no hay pujas.</p>}
                {canWrite && (
                  <div className="flex items-center gap-2">
                    <Input className="flex-1 h-8" placeholder="Postor" value={d.bidder_name || ""} disabled={busy} onChange={(e) => setDraft((p) => ({ ...p, [t.team_id]: { ...p[t.team_id], bidder_name: e.target.value } }))} data-testid={`agogo-bid-name-${t.team_id}`} />
                    <Input className={`w-28 h-8 ${invalid ? "border-red-500 text-red-600" : ""}`} type="number" placeholder="Monto" value={d.amount || ""} disabled={busy} onChange={(e) => setDraft((p) => ({ ...p, [t.team_id]: { ...p[t.team_id], amount: e.target.value } }))} onKeyDown={(e) => { if (e.key === "Enter") addBid(t.team_id); }} data-testid={`agogo-bid-amount-${t.team_id}`} />
                    <Button size="sm" disabled={busy} onClick={() => addBid(t.team_id)} data-testid={`agogo-add-bid-${t.team_id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Pujar</Button>
                  </div>
                )}
                {canWrite && invalid && <p className="text-[11px] text-red-600 mt-1" data-testid={`agogo-bid-warn-${t.team_id}`}>{highest != null ? `Debe superar la puja más alta (${money(highest)})` : `Por debajo del precio de salida (${money(t.starting_price)})`}</p>}
              </div>
            );
          })}
          {teams.length === 0 && <Empty text="Sin equipos para subastar." />}
        </div>
      </Section>

      <Section title="Resumen de la bolsa">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Stat label="Bolsa total" value={money(summary.total_raised)} testid="agogo-pool-total" />
          <Stat label="Cortes" value={money(summary.house_total)} />
          <Stat label="Bolsa neta" value={money(summary.net_pool)} testid="agogo-pool-net" />
        </div>
        {summary.house_cuts?.length > 0 && (
          <div className="mb-3 text-sm text-[#64748B]">{summary.house_cuts.map((h, i) => <span key={i} className="mr-3">{h.label}: {money(h.amount)} ({h.percent}%)</span>)}</div>
        )}
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm" data-testid="agogo-payout-table">
            <thead className="bg-gray-50 text-[#64748B]"><tr>
              <th className="px-3 py-2 text-left">Lugar</th><th className="px-3 py-2 text-left">Equipo</th>
              <th className="px-3 py-2 text-left">Dueño</th><th className="px-3 py-2 text-right">%</th><th className="px-3 py-2 text-right">Premio</th>
            </tr></thead>
            <tbody>
              {summary.payouts.map((p) => (
                <tr key={p.position} className="border-t">
                  <td className="px-3 py-2 font-bold">{p.position}º</td>
                  <td className="px-3 py-2">{p.team_name || <span className="text-[#94A3B8]">por definir</span>}</td>
                  <td className="px-3 py-2">{p.owner_name || "—"}</td>
                  <td className="px-3 py-2 text-right">{p.percent}%</td>
                  <td className="px-3 py-2 text-right font-bold text-[#1B4D3E]">{money(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-gray-200 rounded-xl p-3 text-center">
      <div className="text-[11px] text-[#94A3B8] uppercase">{label}</div>
      <div className="text-lg font-bold text-[#0F172A]" data-testid={testid}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config (the most important part — fully editable per club)
// ---------------------------------------------------------------------------
const RULE_LABELS = {
  min_hcp_sum: "Suma mínima de HCP del equipo",
  max_individual_hcp: "Tope de HCP individual",
  min_ladies: "Mínimo de damas",
  max_pros: "Máximo de profesionales",
  max_seniors_gold: "Máximo seniors en doradas",
  pro_lowest3_min: "Con pro: suma de 3 HCP más bajos ≥",
  max_low_hcp: "Máximo jugadores con HCP bajo",
};

function ConfigTab({ tid, canWrite, onChange }) {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, t] = await Promise.all([
      api(`/club/tournaments/${tid}/agogo-config`).then((r) => r.json()),
      api(`/club/agogo/templates`).then((r) => r.json()),
    ]);
    setCfg(c.config); setTemplates(Array.isArray(t) ? t : []);
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setCfg((p) => ({ ...p, ...patch }));
  const setRule = (k, patch) => setCfg((p) => ({ ...p, rules: { ...p.rules, [k]: { ...p.rules[k], ...patch } } }));
  const setTee = (i, patch) => setCfg((p) => ({ ...p, tee_marks: p.tee_marks.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));

  const applyTemplate = async (templateId) => {
    const res = await api(`/club/tournaments/${tid}/agogo-config`, { method: "PUT", body: JSON.stringify({ template_id: templateId }) });
    if (res.ok) { const d = await res.json(); setCfg(d.config); toast.success("Plantilla aplicada"); onChange?.(); } else toast.error("Error");
  };
  const seedCanastas = async () => {
    const res = await api(`/club/agogo/templates/seed-canastas`, { method: "POST" });
    if (res.ok) { const t = await res.json(); await applyTemplate(t.template_id); load(); } else toast.error("Error");
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await api(`/club/tournaments/${tid}/agogo-config`, { method: "PUT", body: JSON.stringify({ config: cfg }) });
      if (!res.ok) throw new Error("Error");
      toast.success("Configuración guardada"); onChange?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nombre de la plantilla:");
    if (!name) return;
    const res = await api(`/club/agogo/templates`, { method: "POST", body: JSON.stringify({ name, config: cfg }) });
    if (res.ok) { toast.success("Plantilla guardada"); load(); } else toast.error("Error");
  };

  if (!cfg) return <Spinner />;
  const num = (label, key, testid) => (
    <div><Label className="text-xs">{label}</Label><Input type="number" value={cfg[key] ?? ""} disabled={!canWrite} onChange={(e) => set({ [key]: parseInt(e.target.value) || 0 })} data-testid={testid} /></div>
  );

  return (
    <div className="space-y-6" data-testid="agogo-config">
      <div className="flex flex-wrap gap-2 items-center">
        <Label className="text-xs">Plantillas:</Label>
        {templates.map((t) => <Button key={t.template_id} variant="outline" size="sm" onClick={() => applyTemplate(t.template_id)} data-testid={`agogo-apply-tpl-${t.template_id}`}>{t.name}</Button>)}
        {canWrite && <Button variant="outline" size="sm" onClick={seedCanastas} data-testid="agogo-seed-canastas">+ Canastas GCC</Button>}
      </div>

      <Section title="Estructura">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {num("Integrantes por equipo", "team_size", "agogo-cfg-team-size")}
          {num("Cupo de equipos", "team_cap", "agogo-cfg-team-cap")}
          {num("Equipos que pasan el corte", "cut_teams", "agogo-cfg-cut")}
          {num("Rondas", "rounds", "agogo-cfg-rounds")}
          {num("Par total (36h)", "par_total", "agogo-cfg-par-total")}
          {num("Par por ronda", "par_per_round", "agogo-cfg-par-round")}
          {num("Lugares oyes por hoyo", "oyeses_places", "agogo-cfg-oyes-places")}
          <div><Label className="text-xs">Cierre inscripción (fecha)</Label><Input type="date" value={cfg.registration_deadline || ""} disabled={!canWrite} onChange={(e) => set({ registration_deadline: e.target.value })} data-testid="agogo-cfg-deadline" /></div>
        </div>
        <div className="mt-3"><Label className="text-xs">Hoyos de oyeses (separados por coma)</Label>
          <Input value={(cfg.oyeses_holes || []).join(", ")} disabled={!canWrite} onChange={(e) => set({ oyeses_holes: e.target.value.split(",").map((s) => parseInt(s.trim())).filter(Boolean) })} data-testid="agogo-cfg-oyes-holes" /></div>
      </Section>

      <Section title="Reglas de inscripción (avisos, no bloquean)">
        <div className="space-y-2">
          {Object.keys(RULE_LABELS).map((k) => (
            <div key={k} className="flex items-center gap-3 flex-wrap" data-testid={`agogo-rule-${k}`}>
              <label className="flex items-center gap-1.5 text-sm min-w-[260px]">
                <input type="checkbox" checked={!!cfg.rules[k]?.enabled} disabled={!canWrite} onChange={(e) => setRule(k, { enabled: e.target.checked })} data-testid={`agogo-rule-${k}-enabled`} />
                {RULE_LABELS[k]}
              </label>
              <Input type="number" className="w-24 h-8" value={cfg.rules[k]?.value ?? ""} disabled={!canWrite} onChange={(e) => setRule(k, { value: parseInt(e.target.value) || 0 })} data-testid={`agogo-rule-${k}-value`} />
              {k === "max_low_hcp" && (
                <span className="text-xs text-[#64748B] flex items-center gap-1">umbral HCP ≤ <Input type="number" className="w-20 h-8" value={cfg.rules[k]?.threshold ?? 7} disabled={!canWrite} onChange={(e) => setRule(k, { threshold: parseInt(e.target.value) || 0 })} data-testid="agogo-rule-max_low_hcp-threshold" /></span>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Marcas de salida (slope / rating / par)">
        <div className="space-y-2">
          {cfg.tee_marks.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`agogo-tee-${m.key}`}>
              <Input className="col-span-3 h-8" placeholder="Nombre" value={m.name} disabled={!canWrite} onChange={(e) => setTee(i, { name: e.target.value })} />
              <Input className="col-span-2 h-8" placeholder="Slope" value={m.slope ?? ""} disabled={!canWrite} onChange={(e) => setTee(i, { slope: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`agogo-tee-${m.key}-slope`} />
              <Input className="col-span-2 h-8" placeholder="Rating" value={m.rating ?? ""} disabled={!canWrite} onChange={(e) => setTee(i, { rating: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`agogo-tee-${m.key}-rating`} />
              <Input className="col-span-2 h-8" placeholder="Par" value={m.par ?? ""} disabled={!canWrite} onChange={(e) => setTee(i, { par: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`agogo-tee-${m.key}-par`} />
              <span className="col-span-3 text-[10px] text-[#94A3B8] truncate">{m.criteria}</span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#94A3B8] mt-1">Si dejas slope/rating/par vacíos, se usan las marcas del campo del torneo.</p>
      </Section>

      <Section title="Textos informativos (cuota / premios / reglas) — sin cobros">
        {[["fee_text", "Cuota"], ["prizes_text", "Premios"], ["rules_text", "Reglas"]].map(([k, l]) => (
          <div key={k} className="mb-2"><Label className="text-xs">{l}</Label>
            <textarea className="w-full border rounded p-2 text-sm" rows={2} value={cfg[k] || ""} disabled={!canWrite} onChange={(e) => set({ [k]: e.target.value })} data-testid={`agogo-cfg-${k}`} /></div>
        ))}
      </Section>

      {canWrite && (
        <div className="flex gap-2 sticky bottom-0 bg-white py-3 border-t">
          <Button onClick={save} disabled={saving} data-testid="agogo-save-config-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Guardando…" : "Guardar configuración"}</Button>
          <Button variant="outline" onClick={saveAsTemplate} data-testid="agogo-save-template-btn">Guardar como plantilla</Button>
        </div>
      )}
    </div>
  );
}

function InfoTab({ tid }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api(`/club/tournaments/${tid}/agogo-config`).then((r) => r.json()).then((d) => setCfg(d.config)); }, [tid]);
  if (!cfg) return <Spinner />;
  const block = (title, text) => text ? (
    <div className="border border-gray-200 rounded-xl p-4"><h4 className="font-bold text-[#0F172A] mb-1">{title}</h4><p className="text-sm text-[#64748B] whitespace-pre-wrap">{text}</p></div>
  ) : null;
  return (
    <div className="space-y-3" data-testid="agogo-info">
      {block("Cuota", cfg.fee_text)}
      {block("Premios", cfg.prizes_text)}
      {block("Reglas", cfg.rules_text)}
      {!cfg.fee_text && !cfg.prizes_text && !cfg.rules_text && <Empty text="Sin textos informativos. Agrégalos en Configuración." />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-[#0F172A] mb-2">{title}</h3>
      <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/40">{children}</div>
    </div>
  );
}
function Spinner() { return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>; }
function Empty({ text }) { return <div className="p-8 text-center text-sm text-[#94A3B8]">{text}</div>; }
